import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { AlertTriangle, Settings, X, Clock, TrendingUp, TrendingDown, Send, ChevronRight, ChevronDown } from 'lucide-react';
import axios from 'axios';
import config from '../config';
import { variationColor } from '../utils/variationUtils';

const API_URL = config.API_URL;

const METRIQUES = [
  { key: 'clicks', label: 'Clics' },
  { key: 'cost', label: 'Budget' }
];

const formatValeur = (metric, value) => (
  metric === 'cost'
    ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value || 0)
    : Math.round(value || 0).toLocaleString('fr-FR')
);

const formatEcart = (value) => (
  Number.isFinite(value) ? `${value > 0 ? '+' : ''}${Math.round(value)} %` : '—'
);

/**
 * L'autorisation d'afficher des notifications, telle que le navigateur la voit.
 *
 * Elle ne se déduit pas de la case cochée : la case vit en base, l'autorisation vit dans le
 * navigateur, et les deux se contredisent sans se le dire. Le même compte peut être autorisé
 * au bureau et bloqué à la maison — d'où une lecture à chaque affichage, jamais un stockage.
 *
 * Quatre états, dont trois demandent une conduite différente :
 * - `absente` : navigateur sans l'API. Rien à proposer.
 * - `default` : jamais demandée. Un clic suffit.
 * - `granted` : la case dit vrai.
 * - `denied` : refusée. `requestPermission()` renvoie « denied » sans rien afficher ; seuls
 *   les réglages du navigateur débloquent, il faut donc le dire au lieu de le retenter.
 */
const lirePermission = () => (
  typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'absente'
);

// Styles partagés de la modale. Regroupés ici pour que toutes les sections s'alignent sur
// la même grille et que les champs aient la même hauteur — sans cela, chaque section
// démarre à une abscisse différente et l'ensemble paraît inachevé.
const LARGEUR_LIBELLE = '96px';

const sectionStyle = {
  marginBottom: '20px',
  paddingBottom: '20px',
  borderBottom: '1px solid #f3f4f6'
};

/** Grille commune : colonne de libellés à largeur fixe, puis les contrôles. */
const grille = (colonnes) => ({
  display: 'grid',
  gridTemplateColumns: `${LARGEUR_LIBELLE} ${colonnes}`,
  gap: '10px 16px',
  alignItems: 'center'
});

const titreStyle = { fontSize: '14px', fontWeight: 600, color: '#111827', margin: '0 0 4px 0' };

const aideStyle = { fontSize: '12px', color: '#6b7280', margin: '0 0 14px 0', lineHeight: 1.5 };

const enteteColonneStyle = {
  fontSize: '11px', fontWeight: 600, color: '#9ca3af',
  textTransform: 'uppercase', letterSpacing: '0.03em'
};

const libelleStyle = { fontSize: '13px', fontWeight: 500, color: '#374151' };

const champStyle = {
  width: '88px', height: '34px', padding: '0 10px', boxSizing: 'border-box',
  border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px',
  color: '#111827', background: 'white'
};

const champAvecSuffixe = { display: 'flex', alignItems: 'center', gap: '8px' };

const suffixeStyle = { fontSize: '12px', color: '#6b7280', whiteSpace: 'nowrap' };

const boutonDiscret = {
  padding: '5px 10px', fontSize: '12px', background: 'white',
  border: '1px solid #d1d5db', borderRadius: '6px', color: '#374151', cursor: 'pointer'
};

const caseStyle = {
  display: 'flex', alignItems: 'center', gap: '10px',
  fontSize: '13px', color: '#374151', marginBottom: '8px', cursor: 'pointer'
};

/**
 * Alertes de dépassement — carte de la vue Notifications.
 *
 * Compare les 24 dernières heures de chaque compte Google Ads à la moyenne quotidienne des
 * jours précédents, et prévient par e-mail et par notification navigateur.
 */
const MetricAlertsCard = ({ token }) => {
  const [prefs, setPrefs] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  // Comptes dépliés dans la modale : réglages propres à un compte
  const [comptesDeplies, setComptesDeplies] = useState({});
  const [permission, setPermission] = useState(lirePermission);

  const entetes = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const chargerPrefs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/alerts/preferences`, entetes());
      if (data.success) setPrefs(data.preferences);
    } catch (error) {
      console.error('Erreur chargement préférences alertes:', error);
    }
  }, [entetes]);

  const chargerComptes = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/alerts/accounts`, entetes());
      if (data.success) setAccounts(data.accounts);
    } catch (error) {
      console.error('Erreur chargement comptes surveillés:', error);
    }
  }, [entetes]);

  const chargerAlertes = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/alerts`, entetes());
      if (data.success) setAlerts(data.open || []);
    } catch (error) {
      console.error('Erreur chargement alertes:', error);
    }
  }, [entetes]);

  useEffect(() => {
    if (!token) return;
    chargerPrefs();
    chargerComptes();
    chargerAlertes();
  }, [token, chargerPrefs, chargerComptes, chargerAlertes]);

  /**
   * Relire l'autorisation au retour sur l'onglet.
   *
   * Débloquer Kokpit dans les réglages du navigateur ne déclenche aucun événement : sans
   * cette relecture, l'avertissement resterait affiché après que l'utilisateur l'a suivi,
   * ce qui est le meilleur moyen de lui faire croire qu'il n'a pas marché.
   */
  useEffect(() => {
    const relire = () => setPermission(lirePermission());
    window.addEventListener('focus', relire);
    document.addEventListener('visibilitychange', relire);
    return () => {
      window.removeEventListener('focus', relire);
      document.removeEventListener('visibilitychange', relire);
    };
  }, []);

  /** Demande l'autorisation ; sans effet si elle a déjà été refusée. */
  const demanderPermission = async () => {
    if (lirePermission() !== 'default') return lirePermission();
    try { await Notification.requestPermission(); } catch { /* refus : l'e-mail suffit */ }
    const etat = lirePermission();
    setPermission(etat);
    return etat;
  };

  const enregistrer = async (updates) => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API_URL}/api/alerts/preferences`, updates, entetes());
      if (data.success) setPrefs(data.preferences);
    } catch (error) {
      console.error('Erreur enregistrement préférences alertes:', error);
    } finally {
      setSaving(false);
    }
  };

  const activer = async (actif) => {
    // Demander l'autorisation au moment de l'activation : un navigateur refuse une
    // demande qui ne fait pas suite à une action de l'utilisateur.
    if (actif) await demanderPermission();
    await enregistrer({ isActive: actif });
  };

  const majSeuil = (metric, sens, valeur) => {
    const nombre = Number(valeur);
    if (!Number.isFinite(nombre)) return;
    enregistrer({
      thresholds: {
        ...prefs.thresholds,
        [metric]: { ...prefs.thresholds[metric], [sens]: nombre }
      }
    });
  };

  /** Applique une surcharge à un compte. Un champ à `null` le remet en héritage. */
  const majCompte = async (accountId, patch) => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/alerts/accounts/${accountId}`, patch, entetes());
      await chargerComptes();
    } catch (error) {
      console.error('Erreur mise à jour du compte:', error);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Surcharge un seuil de compte. Vider le champ renvoie `null`, ce qui rétablit
   * l'héritage du réglage général plutôt que d'enregistrer un zéro.
   */
  const majSeuilCompte = (compte, metric, sens, valeur) => {
    const actuels = compte.overrides?.thresholds || {};
    const pourMetrique = { ...(actuels[metric] || {}) };

    if (valeur === '') delete pourMetrique[sens];
    else pourMetrique[sens] = Number(valeur);

    const suivants = { ...actuels, [metric]: pourMetrique };
    if (Object.keys(pourMetrique).length === 0) delete suivants[metric];

    majCompte(compte.accountId, {
      thresholds: Object.keys(suivants).length === 0 ? null : suivants
    });
  };

  /** Même principe pour un volume plancher. */
  const majVolumeCompte = (compte, metric, valeur) => {
    const suivants = { ...(compte.overrides?.minVolume || {}) };
    if (valeur === '') delete suivants[metric];
    else suivants[metric] = Number(valeur);

    majCompte(compte.accountId, {
      minVolume: Object.keys(suivants).length === 0 ? null : suivants
    });
  };

  /**
   * Coche ou décoche tous les comptes d'un coup.
   * Les appels sont séquentiels : une salve de dix-neuf requêtes concurrentes sur la même
   * table produirait des écritures concurrentes pour un gain imperceptible.
   */
  const toutSelectionner = async (surveille) => {
    setSaving(true);
    try {
      for (const compte of accounts) {
        if ((compte.settings?.isWatched !== false) === surveille) continue;
        await axios.put(`${API_URL}/api/alerts/accounts/${compte.accountId}`,
          { isWatched: surveille }, entetes());
      }
      await chargerComptes();
    } catch (error) {
      console.error('Erreur sélection des comptes:', error);
    } finally {
      setSaving(false);
    }
  };

  /** Supprime toute surcharge : le compte repasse intégralement au réglage général. */
  const reinitCompte = async (accountId) => {
    setSaving(true);
    try {
      await axios.delete(`${API_URL}/api/alerts/accounts/${accountId}`, entetes());
      await chargerComptes();
    } catch (error) {
      console.error('Erreur réinitialisation du compte:', error);
    } finally {
      setSaving(false);
    }
  };

  const envoyerTest = async () => {
    setTestLoading(true);
    setTestResult(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/alerts/test-email`, {}, entetes());
      setTestResult({ ok: data.success, message: data.message || data.error });
    } catch (error) {
      setTestResult({ ok: false, message: error.response?.data?.error || 'Échec de l\'envoi' });
    } finally {
      setTestLoading(false);
    }
  };

  if (!prefs) return null;

  const surveilles = accounts.filter(c => c.settings?.isWatched !== false).length;

  return (
    <>
      <div className="notification-card">
        <div className="notification-card-header">
          <AlertTriangle size={24} />
          <h2>Alertes de trafic</h2>
          {prefs.isActive && (
            <button
              className="config-btn"
              onClick={() => setShowModal(true)}
              style={{
                marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px',
                padding: '8px 14px', background: '#1a1a1a', color: 'white', border: 'none',
                borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer'
              }}
            >
              <Settings size={16} />
              Paramétrer
            </button>
          )}
        </div>

        <div className="notification-card-body">
          <div className="notification-option">
            <div className="notification-option-info">
              <h3>Surveiller les écarts de trafic</h3>
              <p>
                Compare les 24 dernières heures de vos comptes Google Ads à la moyenne
                quotidienne des {prefs.baselineWindowDays} jours précédents, et vous
                prévient dès qu'un compte sort de son rythme habituel.
              </p>
              <div className="notification-schedule">
                <Clock size={16} />
                <span>
                  Vérification toutes les heures — {surveilles} compte(s) surveillé(s)
                  {surveilles === 0 && ' : tous décochés'}
                </span>
              </div>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={prefs.isActive}
                onChange={(e) => activer(e.target.checked)}
                disabled={saving}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          {prefs.isActive && alerts.length > 0 && (
            <div style={{ marginTop: '16px', borderTop: '1px solid #f3f4f6', paddingTop: '16px' }}>
              <h3 style={{ fontSize: '14px', margin: '0 0 10px 0', color: '#374151' }}>
                En cours ({alerts.length})
              </h3>
              {alerts.map(alerte => {
                const monte = alerte.direction === 'up';
                const Fleche = monte ? TrendingUp : TrendingDown;
                return (
                  <div
                    key={alerte.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '8px 0', borderBottom: '1px solid #f9fafb', fontSize: '13px'
                    }}
                  >
                    <Fleche size={16} style={{ color: variationColor(alerte.deviationPercent, true), flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 500, color: '#111827' }}>{alerte.accountName}</div>
                      <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                        {METRIQUES.find(m => m.key === alerte.metric)?.label}
                        {' · '}
                        {formatValeur(alerte.metric, alerte.windowValue)} sur 24 h contre{' '}
                        {formatValeur(alerte.metric, alerte.baselineValue)} en moyenne
                      </div>
                    </div>
                    <div style={{
                      fontWeight: 700, flexShrink: 0,
                      color: variationColor(alerte.deviationPercent, true)
                    }}>
                      {formatEcart(alerte.deviationPercent)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {showModal && ReactDOM.createPortal(
        <div
          onClick={() => setShowModal(false)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white', borderRadius: '16px', width: '100%', maxWidth: '620px',
              maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column'
            }}
          >
            <div style={{
              padding: '20px 24px', borderBottom: '1px solid #e5e7eb',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between'
            }}>
              <h2 style={{ margin: 0, fontSize: '18px' }}>Paramètres des alertes de trafic</h2>
              <button onClick={() => setShowModal(false)} className="btn-icon"
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>

            <div style={{ padding: '24px', overflowY: 'auto' }}>
              <section style={sectionStyle}>
                <h3 style={titreStyle}>Seuils de déclenchement</h3>
                <p style={aideStyle}>
                  Écart, en pourcentage, entre les 24 dernières heures et la moyenne
                  quotidienne. Une valeur négative correspond à une chute.
                </p>

                {/* Une grille commune à toutes les lignes : les colonnes restent alignées
                    quelle que soit la longueur du libellé. */}
                <div style={grille('1fr 1fr')}>
                  <div />
                  <div style={enteteColonneStyle}>Hausse</div>
                  <div style={enteteColonneStyle}>Chute</div>

                  {METRIQUES.map(({ key, label }) => (
                    <React.Fragment key={key}>
                      <div style={libelleStyle}>{label}</div>
                      <div style={champAvecSuffixe}>
                        <input
                          type="number"
                          value={prefs.thresholds?.[key]?.up ?? 100}
                          onChange={(e) => majSeuil(key, 'up', e.target.value)}
                          disabled={saving}
                          style={champStyle}
                        />
                        <span style={suffixeStyle}>%</span>
                      </div>
                      <div style={champAvecSuffixe}>
                        <input
                          type="number"
                          value={prefs.thresholds?.[key]?.down ?? -50}
                          onChange={(e) => majSeuil(key, 'down', e.target.value)}
                          disabled={saving}
                          style={champStyle}
                        />
                        <span style={suffixeStyle}>%</span>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </section>

              <section style={sectionStyle}>
                <h3 style={titreStyle}>Période de référence</h3>
                <p style={aideStyle}>
                  Nombre de jours complets servant à calculer la moyenne quotidienne.
                </p>
                <div style={grille('1fr')}>
                  <div style={libelleStyle}>Durée</div>
                  <div style={champAvecSuffixe}>
                    <input
                      type="number"
                      min="3"
                      max="90"
                      value={prefs.baselineWindowDays}
                      onChange={(e) => enregistrer({ baselineWindowDays: Number(e.target.value) })}
                      disabled={saving}
                      style={champStyle}
                    />
                    <span style={suffixeStyle}>jours</span>
                  </div>
                </div>
              </section>

              <section style={sectionStyle}>
                <h3 style={titreStyle}>Volume minimal</h3>
                <p style={aideStyle}>
                  Sous cette moyenne quotidienne, la métrique n'est pas évaluée : sur un
                  très petit volume, le moindre écart dépasse n'importe quel seuil.
                </p>
                <div style={grille('1fr')}>
                  {METRIQUES.map(({ key, label }) => (
                    <React.Fragment key={key}>
                      <div style={libelleStyle}>{label}</div>
                      <div style={champAvecSuffixe}>
                        <input
                          type="number"
                          min="0"
                          value={prefs.minVolume?.[key] ?? 10}
                          onChange={(e) => enregistrer({
                            minVolume: { ...prefs.minVolume, [key]: Number(e.target.value) }
                          })}
                          disabled={saving}
                          style={champStyle}
                        />
                        <span style={suffixeStyle}>
                          {key === 'cost' ? '€ par jour' : 'clics par jour'}
                        </span>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </section>

              <section style={sectionStyle}>
                <h3 style={titreStyle}>Canaux</h3>
                <label style={caseStyle}>
                  <input
                    type="checkbox"
                    checked={prefs.emailEnabled}
                    onChange={(e) => enregistrer({ emailEnabled: e.target.checked })}
                    disabled={saving}
                  />
                  <span>E-mail</span>
                </label>
                <label style={caseStyle}>
                  <input
                    type="checkbox"
                    checked={prefs.popinEnabled}
                    onChange={(e) => enregistrer({ popinEnabled: e.target.checked })}
                    disabled={saving}
                  />
                  <span>Pop-in dans Kokpit</span>
                </label>
                <label style={caseStyle}>
                  <input
                    type="checkbox"
                    checked={prefs.browserEnabled}
                    onChange={async (e) => {
                      // La valeur se lit **avant** l'attente. La case est contrôlée : pendant
                      // que le navigateur affiche sa demande, React a déjà remis le DOM sur
                      // l'ancienne valeur, et `e.target.checked` relu ensuite renvoie le
                      // contraire du clic — la case redevenait impossible à cocher.
                      const actif = e.target.checked;
                      // Le clic sur la case est le geste qui exprime l'intention, et c'est
                      // aussi le seul moment où le navigateur accepte d'afficher sa demande.
                      // La demande ne partait qu'à l'activation de l'outil : ceux qui
                      // l'avaient activé plus tôt gardaient une case cochée sans autorisation,
                      // et ne recevaient rien, en silence.
                      if (actif) await demanderPermission();
                      enregistrer({ browserEnabled: actif });
                    }}
                    disabled={saving}
                  />
                  <span>Notification système</span>
                </label>
                {/* Chaque canal est nommé par le moment où il agit : c'est ce qui rend la
                    combinaison lisible sans l'expliquer. */}
                <p style={{ ...aideStyle, margin: '6px 0 0 26px' }}>
                  La pop-in vous prévient pendant que vous êtes dans Kokpit, la notification
                  système pendant que vous êtes ailleurs, et l'e-mail quand le navigateur est
                  fermé.
                </p>

                {/* L'état de l'autorisation, quand il contredit la case. La cloche, elle,
                    continue de lister les alertes dans tous les cas : seule la bulle du
                    système est en jeu ici. */}
                {prefs.browserEnabled && permission !== 'granted' && (
                  <div style={{
                    margin: '8px 0 0 26px', padding: '10px 12px', borderRadius: '6px',
                    backgroundColor: '#fffbeb', border: '1px solid #fde68a',
                    fontSize: '12px', color: '#92400e', lineHeight: 1.5
                  }}>
                    {permission === 'default' && (
                      <>
                        Votre navigateur n'a pas encore autorisé les notifications système :
                        vous ne serez pas prévenu des alertes survenues pendant que Kokpit est
                        en arrière-plan.{' '}
                        {prefs.popinEnabled && 'La pop-in vous les montrera à votre retour.'}
                        <button
                          onClick={demanderPermission}
                          style={{
                            display: 'block', marginTop: '8px', padding: '6px 12px',
                            border: '1px solid #d97706', borderRadius: '6px',
                            backgroundColor: 'white', color: '#92400e',
                            fontSize: '12px', fontWeight: 600, cursor: 'pointer'
                          }}
                        >
                          Autoriser les notifications
                        </button>
                      </>
                    )}
                    {permission === 'denied' && (
                      <>
                        Les notifications système sont bloquées pour Kokpit dans ce navigateur.
                        {prefs.popinEnabled
                          ? " Une alerte survenue en votre absence s'affichera à votre retour sur l'onglet ; pour être prévenu sur le moment, autorisez"
                          : ' Pour être prévenu des alertes survenues en votre absence, autorisez'}
                        {' '}les notifications dans les réglages de site — l'icône à gauche de
                        l'adresse.
                      </>
                    )}
                    {permission === 'absente' && (
                      <>
                        Ce navigateur ne sait pas afficher de notifications système.
                        {prefs.popinEnabled
                          ? ' La pop-in et l\'e-mail couvrent le reste.'
                          : " L'e-mail reste le seul canal quand Kokpit n'est pas au premier plan."}
                      </>
                    )}
                  </div>
                )}
              </section>

              <section style={{ ...sectionStyle, borderBottom: 'none', marginBottom: 0, paddingBottom: 0 }}>
                <h3 style={titreStyle}>
                  Comptes surveillés <span style={{ color: '#9ca3af', fontWeight: 400 }}>({surveilles}/{accounts.length})</span>
                </h3>
                <p style={aideStyle}>
                  Tous les comptes sont surveillés et suivent le réglage général ci-dessus.
                  Décochez ceux que vous ne voulez pas suivre, ou dépliez-en un pour lui
                  donner ses propres valeurs.
                </p>

                {/* Sans compte coché, l'outil peut être actif et ne rien faire : autant
                    le dire, plutôt que de laisser croire à une surveillance en place. */}
                {surveilles === 0 && (
                  <div style={{
                    padding: '8px 12px', marginBottom: '10px', borderRadius: '6px',
                    background: '#fef3c7', border: '1px solid #fde68a',
                    fontSize: '12px', color: '#92400e'
                  }}>
                    Tous les comptes ont été décochés : l'outil ne produira aucune alerte.
                  </div>
                )}

                <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                  <button
                    type="button"
                    onClick={() => toutSelectionner(true)}
                    disabled={saving || surveilles === accounts.length}
                    style={boutonDiscret}
                  >
                    Tout sélectionner
                  </button>
                  <button
                    type="button"
                    onClick={() => toutSelectionner(false)}
                    disabled={saving || surveilles === 0}
                    style={boutonDiscret}
                  >
                    Tout désélectionner
                  </button>
                </div>

                <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', maxHeight: '320px', overflowY: 'auto' }}>
                  {accounts.map((compte, index) => {
                    const surveille = compte.settings?.isWatched !== false;
                    const deplie = comptesDeplies[compte.accountId];
                    return (
                      <div key={compte.accountId} style={{ borderTop: index === 0 ? 'none' : '1px solid #f3f4f6' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', height: '40px', padding: '0 12px' }}>
                          <input
                            type="checkbox"
                            checked={surveille}
                            onChange={(e) => majCompte(compte.accountId, { isWatched: e.target.checked })}
                            disabled={saving}
                            style={{ flexShrink: 0 }}
                            title={surveille ? 'Surveillé' : 'Exclu'}
                          />
                          <span style={{
                            flex: 1, minWidth: 0, fontSize: '13px',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            color: surveille ? '#111827' : '#9ca3af'
                          }}>
                            {compte.name}
                          </span>

                          {/* Signale d'un coup d'œil les comptes qui ne suivent plus le
                              réglage général — sans cela, une surcharge oubliée est
                              invisible tant qu'on n'a pas déplié chaque ligne. */}
                          {compte.settings?.hasOverrides && (
                            <span style={{
                              flexShrink: 0, fontSize: '10px', fontWeight: 600,
                              padding: '2px 7px', borderRadius: '10px',
                              background: '#eef2ff', color: '#4338ca'
                            }}>
                              personnalisé
                            </span>
                          )}

                          <button
                            type="button"
                            onClick={() => setComptesDeplies(prev => ({ ...prev, [compte.accountId]: !prev[compte.accountId] }))}
                            style={{
                              flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer',
                              color: '#6b7280', display: 'flex', alignItems: 'center', padding: '4px'
                            }}
                            title="Réglages propres à ce compte"
                          >
                            {deplie ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          </button>
                        </div>

                        {deplie && (
                          <div style={{ padding: '4px 12px 14px 36px', background: '#fafafa' }}>
                            <p style={{ ...aideStyle, margin: '0 0 12px 0' }}>
                              Un champ laissé vide suit le réglage général, dont la valeur
                              est rappelée en gris.
                            </p>

                            <div style={grille('1fr 1fr')}>
                              <div />
                              <div style={enteteColonneStyle}>Hausse</div>
                              <div style={enteteColonneStyle}>Chute</div>
                              {METRIQUES.map(({ key, label }) => (
                                <React.Fragment key={key}>
                                  <div style={libelleStyle}>{label}</div>
                                  {['up', 'down'].map(sens => (
                                    <div key={sens} style={champAvecSuffixe}>
                                      <input
                                        type="number"
                                        value={compte.overrides?.thresholds?.[key]?.[sens] ?? ''}
                                        placeholder={String(prefs.thresholds?.[key]?.[sens] ?? '')}
                                        onChange={(e) => majSeuilCompte(compte, key, sens, e.target.value)}
                                        disabled={saving}
                                        style={champStyle}
                                      />
                                      <span style={suffixeStyle}>%</span>
                                    </div>
                                  ))}
                                </React.Fragment>
                              ))}
                            </div>

                            <div style={{ ...grille('1fr'), marginTop: '12px' }}>
                              <div style={libelleStyle}>Référence</div>
                              <div style={champAvecSuffixe}>
                                <input
                                  type="number"
                                  min="3"
                                  max="90"
                                  value={compte.overrides?.baselineWindowDays ?? ''}
                                  placeholder={String(prefs.baselineWindowDays)}
                                  onChange={(e) => majCompte(compte.accountId, {
                                    baselineWindowDays: e.target.value === '' ? null : Number(e.target.value)
                                  })}
                                  disabled={saving}
                                  style={champStyle}
                                />
                                <span style={suffixeStyle}>jours</span>
                              </div>

                              {METRIQUES.map(({ key, label }) => (
                                <React.Fragment key={key}>
                                  <div style={libelleStyle}>Min. {label.toLowerCase()}</div>
                                  <div style={champAvecSuffixe}>
                                    <input
                                      type="number"
                                      min="0"
                                      value={compte.overrides?.minVolume?.[key] ?? ''}
                                      placeholder={String(prefs.minVolume?.[key] ?? '')}
                                      onChange={(e) => majVolumeCompte(compte, key, e.target.value)}
                                      disabled={saving}
                                      style={champStyle}
                                    />
                                    <span style={suffixeStyle}>
                                      {key === 'cost' ? '€ par jour' : 'clics par jour'}
                                    </span>
                                  </div>
                                </React.Fragment>
                              ))}
                            </div>

                            <div style={{ marginTop: '14px' }}>
                              <div style={{ ...enteteColonneStyle, marginBottom: '6px' }}>Canaux</div>
                              {[
                                { champ: 'emailEnabled', label: 'E-mail' },
                                { champ: 'popinEnabled', label: 'Pop-in dans Kokpit' },
                                { champ: 'browserEnabled', label: 'Notification système' }
                              ].map(({ champ, label }) => (
                                <label key={champ} style={{ ...caseStyle, marginBottom: '6px' }}>
                                  <input
                                    type="checkbox"
                                    checked={compte.settings?.[champ] !== false}
                                    onChange={(e) => majCompte(compte.accountId, { [champ]: e.target.checked })}
                                    disabled={saving}
                                  />
                                  <span>
                                    {label}
                                    {compte.overrides?.[champ] == null && (
                                      <span style={{ color: '#9ca3af', fontSize: '11px', marginLeft: '6px' }}>
                                        (hérité)
                                      </span>
                                    )}
                                  </span>
                                </label>
                              ))}
                            </div>

                            {compte.settings?.hasOverrides && (
                              <button
                                type="button"
                                onClick={() => reinitCompte(compte.accountId)}
                                disabled={saving}
                                style={{
                                  marginTop: '12px', padding: '5px 10px', fontSize: '12px',
                                  background: 'white', border: '1px solid #d1d5db',
                                  borderRadius: '6px', color: '#374151', cursor: 'pointer'
                                }}
                              >
                                Revenir au réglage général
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            </div>

            <div className="modal-footer" style={{
              padding: '16px 24px', borderTop: '1px solid #e5e7eb',
              display: 'flex', alignItems: 'center', gap: '12px'
            }}>
              <button
                onClick={envoyerTest}
                disabled={testLoading}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px',
                  background: 'white', border: '1px solid #d1d5db', borderRadius: '8px',
                  fontSize: '13px', cursor: 'pointer'
                }}
              >
                <Send size={14} />
                {testLoading ? 'Envoi…' : 'Envoyer un e-mail de test'}
              </button>
              {testResult && (
                <span style={{ fontSize: '12px', color: testResult.ok ? '#10b981' : '#ef4444' }}>
                  {testResult.message}
                </span>
              )}
              <button
                onClick={() => setShowModal(false)}
                className="btn-primary"
                style={{
                  marginLeft: 'auto', padding: '8px 18px', background: '#1a1a1a',
                  color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer'
                }}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default MetricAlertsCard;
